import { useCallback, useEffect, useRef } from 'react'
import gsap from 'gsap'

const animateAutosaveIndicator = (
  target: HTMLElement | null
): gsap.core.Timeline | undefined => {
  if (!target) return undefined

  return gsap
    .timeline()
    .set(target, { opacity: 0, y: -4 })
    .to(target, { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' })
    .to(target, { opacity: 0, duration: 0.3, ease: 'power2.in', delay: 1.6 })
}

/**
 * The "Autosaved" badge, shown when a save actually lands.
 *
 * Driven by the caller rather than by a timer or by a save going out of flight: only the
 * caller knows a save resolved successfully, and a badge on a timer claims a save that may
 * never have happened.
 */
export const useAutosaveIndicator = () => {
  const indicatorRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<gsap.core.Timeline>(undefined)

  const showSaved = useCallback(() => {
    timelineRef.current?.kill()
    timelineRef.current = animateAutosaveIndicator(indicatorRef.current)
  }, [])

  useEffect(
    () => () => {
      timelineRef.current?.kill()
    },
    []
  )

  return { indicatorRef, showSaved }
}
